import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import useDemoMode from '../hooks/useDemoMode';

// Blurs its children while demo mode is on. Layout is unchanged — the content is still rendered
// (so widths/heights hold) but unreadable, unselectable and unclickable. Use it for anything that
// gives the practice away (source URLs, practitioner names/photos, Google Business name…); the
// "Practice N" name masking in useDemoMode is separate and stays as is.
// NOTE: for high-volume PII cells (patient names/emails/phones in tables) prefer the
// `data-demo-blur` attribute (DEMO_BLUR_ATTR in useDemoMode) — no extra wrapper or hook per cell.
export const DEMO_BLUR_SX = { filter: 'blur(6px)', userSelect: 'none' };

// For elements you can't wrap (e.g. <Avatar sx>): returns the blur sx while demo mode is on, else null.
export function useDemoBlurSx() {
    const [demo] = useDemoMode();
    return demo ? DEMO_BLUR_SX : null;
}

export default function DemoBlur({ children = null, component = 'span', sx = undefined, ...rest }) {
    const [demo] = useDemoMode();
    if (!demo) return children;
    return (
        <Box
            component={component}
            aria-label="Hidden in demo mode"
            title="Hidden in demo mode"
            sx={{ display: 'inline-block', ...DEMO_BLUR_SX, ...sx }}
            {...rest}
        >
            {children}
        </Box>
    );
}

DemoBlur.propTypes = {
    children: PropTypes.node,
    component: PropTypes.elementType,
    sx: PropTypes.object,
};

// PII attribute helpers. `{...piiProps}` on any element makes the global
// `body.demo-mode [data-demo-blur]` rule blur it (inert when demo mode is off).
export const piiProps = { 'data-demo-blur': '' };

// Tooltip titles render in a portal (outside the tagged trigger), so wrap the TEXT.
export const piiTitle = (text) => (text ? <span {...piiProps}>{text}</span> : text);
